<template>
  <div class="c-project-hero-block">
    <!-- Background Image -->
    <div class="c-project-hero-block__background">
      <img
        :src="backgroundImage.src"
        :alt="backgroundImage.alt"
        class="c-project-hero-block__background-img"
      />
    </div>

    <!-- Buttons Container -->
    <div class="c-project-hero-block__buttons">
      <!-- Floorplan Button -->
      <a
        :href="floorplanPdfUrl || '#'"
        :target="floorplanPdfUrl ? '_blank' : '_self'"
        :rel="floorplanPdfUrl ? 'noopener noreferrer' : ''"
        class="c-project-hero-block__button c-project-hero-block__button--floorplan"
        :aria-label="floorplanButtonText"
      >
        <i class="fa-regular fa-table-layout" aria-hidden="true"></i>
        <span>{{ floorplanButtonText }}</span>
      </a>

      <!-- Gallery Button -->
      <button
        class="c-project-hero-block__button c-project-hero-block__button--gallery"
        @click="openGalleryModal"
        :aria-label="galleryButtonText"
      >
        <i class="fa-regular fa-images" aria-hidden="true"></i>
        <span>{{ galleryButtonText }}</span>
        <span
          v-if="galleryImages.length"
          class="c-project-hero-block__button-badge"
        >
          ({{ galleryImages.length }})
        </span>
      </button>

      <!-- 360 View Button -->
      <button
        v-if="view360Url"
        class="c-project-hero-block__button c-project-hero-block__button--360"
        @click="open360Modal"
        :aria-label="view360ButtonText"
        type="button"
      >
        <i class="fa-regular fa-street-view" aria-hidden="true"></i>
        <span>{{ view360ButtonText }}</span>
      </button>
    </div>

    <!-- Gallery Modal -->
    <div
      v-if="showGalleryModal"
      class="c-project-hero-block__modal"
      @click="closeGalleryModal"
      role="dialog"
      aria-modal="true"
      :aria-label="`${galleryButtonText} modal`"
    >
      <div class="c-project-hero-block__modal-content" @click.stop>
        <!-- Close Button -->
        <button
          class="c-project-hero-block__modal-close"
          @click="closeGalleryModal"
          aria-label="Close gallery modal"
          type="button"
        >
          <i class="fa-regular fa-xmark" aria-hidden="true"></i>
        </button>

        <!-- Main Gallery Slider -->
        <div class="c-project-hero-block__gallery-wrapper">
          <!-- Previous Button -->
          <button
            v-if="hasMultipleImages"
            class="c-project-hero-block__gallery-button c-project-hero-block__gallery-button--prev"
            @click="previousImage"
            aria-label="Previous image"
            type="button"
          >
            <i class="fa-regular fa-arrow-left" aria-hidden="true"></i>
          </button>

          <slide-show
            :id="`project-gallery-${modalId}`"
            :data-active-index="activeImageIndex"
            class="c-project-hero-block__gallery"
          >
            <div class="slider">
              <div
                v-for="(image, index) in galleryImages"
                :key="`slide-${index}`"
                class="slider__item"
              >
                <img
                  :src="image.src"
                  :alt="image.alt"
                  :loading="index === 0 ? 'eager' : 'lazy'"
                />
              </div>
            </div>
          </slide-show>

          <!-- Next Button -->
          <button
            v-if="hasMultipleImages"
            class="c-project-hero-block__gallery-button c-project-hero-block__gallery-button--next"
            @click="nextImage"
            aria-label="Next image"
            type="button"
          >
            <i class="fa-regular fa-arrow-right" aria-hidden="true"></i>
          </button>
        </div>

        <!-- Thumbnails -->
        <div
          v-if="galleryImages.length > 1"
          class="c-project-hero-block__thumbnails"
        >
          <button
            v-for="(image, index) in galleryImages"
            :key="`thumb-${index}`"
            class="c-project-hero-block__thumbnail"
            :class="{ 'is-active': index === activeImageIndex }"
            @click="activeImageIndex = index"
            :aria-label="`View image ${index + 1}`"
            type="button"
          >
            <img :src="image.thumbnail" :alt="`${image.alt} thumbnail`" />
          </button>
        </div>
      </div>
    </div>

    <!-- 360 View Modal -->
    <div
      v-if="show360Modal"
      class="c-project-hero-block__modal"
      @click="close360Modal"
      role="dialog"
      aria-modal="true"
      :aria-label="`${view360ButtonText} modal`"
    >
      <div
        class="c-project-hero-block__modal-content c-project-hero-block__modal-content--360"
        @click.stop
      >
        <!-- Close Button -->
        <button
          class="c-project-hero-block__modal-close"
          @click="close360Modal"
          aria-label="Close 360 view modal"
          type="button"
        >
          <i class="fa-regular fa-xmark" aria-hidden="true"></i>
        </button>

        <div class="c-project-hero-block__iframe-wrapper">
          <iframe
            :src="view360Url"
            :title="view360ButtonText"
            class="c-project-hero-block__iframe"
            allow="fullscreen; accelerometer; gyroscope; xr-spatial-tracking"
            allowfullscreen
            loading="lazy"
          ></iframe>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "VProjectHeroBlock",
  props: {
    /**
     * Background image object
     * @type {Object}
     * @property {String} src - Image URL
     * @property {String} alt - Alt text for accessibility
     */
    backgroundImage: {
      type: Object,
      required: true,
      validator: (obj) => obj.src && obj.alt,
    },

    /**
     * Array of gallery images
     * @type {Array}
     * Each image should have:
     * - src: main image URL
     * - thumbnail: thumbnail image URL
     * - alt: alt text for accessibility
     */
    galleryImages: {
      type: Array,
      default: () => [
        {
          src: "https://picsum.photos/id/1018/1600/900",
          thumbnail: "https://picsum.photos/id/1018/300/200",
          alt: "Projekt vy 1",
        },
        {
          src: "https://picsum.photos/id/1015/1600/900",
          thumbnail: "https://picsum.photos/id/1015/300/200",
          alt: "Projekt vy 2",
        },
        {
          src: "https://picsum.photos/id/1016/1600/900",
          thumbnail: "https://picsum.photos/id/1016/300/200",
          alt: "Projekt vy 3",
        },
      ],
      validator: (arr) =>
        arr.length === 0 ||
        arr.every((img) => img.src && img.thumbnail && img.alt),
    },

    /**
     * Gallery button label text
     */
    galleryButtonText: {
      type: String,
      default: "Bildgalleri",
    },

    /**
     * Floorplan button label text
     */
    floorplanButtonText: {
      type: String,
      default: "Planritning",
    },

    /**
     * URL to the floorplan PDF
     */
    floorplanPdfUrl: {
      type: String,
      default: "https://www.w3.org/WAI/WCAG21/Techniques/pdf/img/table.pdf",
    },

    /**
     * URL to the 360 view, rendered in an iframe. The button is hidden when empty.
     */
    view360Url: {
      type: String,
      default: "",
    },

    /**
     * 360 view button label text
     */
    view360ButtonText: {
      type: String,
      default: "360° vy",
    },
  },

  data() {
    return {
      showGalleryModal: false,
      show360Modal: false,
      activeImageIndex: 0,
      modalId: `modal-${Math.random().toString(36).substr(2, 9)}`,
    };
  },

  computed: {
    hasMultipleImages() {
      return this.galleryImages.length > 1;
    },

    showNavigation() {
      return this.hasMultipleImages;
    },
  },

  methods: {
    openGalleryModal() {
      this.showGalleryModal = true;
      this.activeImageIndex = 0;
      // Prevent body scroll when modal is open
      document.body.style.overflow = "hidden";
    },

    closeGalleryModal() {
      this.showGalleryModal = false;
      // Restore body scroll
      document.body.style.overflow = "";
    },

    open360Modal() {
      this.show360Modal = true;
      document.body.style.overflow = "hidden";
    },

    close360Modal() {
      this.show360Modal = false;
      document.body.style.overflow = "";
    },

    previousImage() {
      if (this.activeImageIndex > 0) {
        this.activeImageIndex--;
      } else {
        this.activeImageIndex = this.galleryImages.length - 1;
      }
    },

    nextImage() {
      if (this.activeImageIndex < this.galleryImages.length - 1) {
        this.activeImageIndex++;
      } else {
        this.activeImageIndex = 0;
      }
    },
  },

  watch: {
    activeImageIndex(newIndex) {
      // Sync with slide-show component by updating data-active-index attribute
      const slideshow = document.querySelector(
        `#project-gallery-${this.modalId}`,
      );
      if (slideshow) {
        slideshow.setAttribute("data-active-index", newIndex.toString());
      }
    },
  },

  beforeDestroy() {
    // Clean up
    document.body.style.overflow = "";
  },

  mounted() {
    // Close modal on Escape key
    const handleEscape = (e) => {
      if (e.key !== "Escape") return;
      if (this.showGalleryModal) {
        this.closeGalleryModal();
      }
      if (this.show360Modal) {
        this.close360Modal();
      }
    };
    document.addEventListener("keydown", handleEscape);

    this.$on("hook:beforeDestroy", () => {
      document.removeEventListener("keydown", handleEscape);
    });
  },
};
</script>

<style scoped>
.c-project-hero-block {
  position: relative;
  width: 100%;
  height: 25rem;
}

@media (min-width: 1200px) {
  .c-project-hero-block {
    height: 31.25rem;
  }
}

/* Background Image */
.c-project-hero-block__background {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 1;
}

.c-project-hero-block__background-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

/* Buttons */
.c-project-hero-block__buttons {
  position: absolute;
  bottom: 2rem;
  right: 2rem;
  z-index: 10;
  display: flex;
  gap: 0.75rem;
  flex-wrap: wrap;
  pointer-events: auto;
  justify-content: flex-end;
}

.c-project-hero-block__button {
  display: flex;
  align-items: center;
  justify-content: space-around;
  gap: 0.25rem;
  padding: 0.625rem 1.25rem;
  background-color: var(--white);
  color: var(--calypso);
  border: none;
  border-radius: 4px;
  font-size: 1rem;
  cursor: pointer;
  text-decoration: none;
  transition: all 0.2s ease;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  position: relative;
  white-space: nowrap;
}

.c-project-hero-block__button > i {
  margin-bottom: 0.25rem;
}

.c-project-hero-block__button:hover {
  background-color: var(--white);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
}

.c-project-hero-block__button:active {
  background-color: #e5e5e5;
}

.c-project-hero-block__button i {
  font-size: 1rem;
}

.c-project-hero-block__button-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: var(--calypso);
  font-size: 1rem;
}

/* Modal */
.c-project-hero-block__modal {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.85);
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  overflow-y: auto;
}

.c-project-hero-block__modal-content {
  position: relative;
  width: 100%;
  max-width: 1200px;
  background-color: #ffffff;
  border-radius: 8px;
  overflow: hidden;
}

.c-project-hero-block__modal-close {
  position: absolute;
  top: 1rem;
  right: 1rem;
  width: 2.5rem;
  height: 2.5rem;
  background-color: var(--white);
  color: var(--calypso);
  border: none;
  border-radius: 50%;
  font-size: 1.25rem;
  cursor: pointer;
  z-index: 10;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
}

.c-project-hero-block__modal-close:hover {
  transform: scale(1.1);
}

/* 360 View */
.c-project-hero-block__iframe-wrapper {
  position: relative;
  width: 100%;
  aspect-ratio: 16 / 9;
  max-height: calc(100vh - 4rem);
}

.c-project-hero-block__iframe {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  border: 0;
  display: block;
}

/* Gallery */
.c-project-hero-block__gallery {
  width: 100%;
  max-height: 600px;
  overflow: hidden;
}

.slider {
  position: relative;
  width: 100%;
}

.slider__item {
  width: 100%;
}

.slider__item img {
  width: 100%;
  height: auto;
  display: block;
}

.c-project-hero-block__gallery-wrapper {
  position: relative;
  width: 100%;
}

.c-project-hero-block__gallery-button {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  width: 2.5rem;
  height: 2.5rem;
  background-color: rgba(255, 255, 255, 0.9);
  color: var(--calypso);
  border: none;
  border-radius: 50%;
  font-size: 1.25rem;
  cursor: pointer;
  z-index: 15;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
}

.c-project-hero-block__gallery-button:hover {
  transform: translateY(-50%) scale(1.1);
}

.c-project-hero-block__gallery-button:active {
  transform: translateY(-50%) scale(0.95);
}

.c-project-hero-block__gallery-button--prev {
  left: 1rem;
}

.c-project-hero-block__gallery-button--next {
  right: 1rem;
}

/* Navigation Indicators */
.slider__nav {
  position: absolute;
  bottom: 1rem;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  gap: 0.5rem;
  z-index: 5;
}

.slider__nav__button {
  width: 12px;
  height: 12px;
  border: none;
  border-radius: 50%;
  background-color: rgba(255, 255, 255, 0.5);
  cursor: pointer;
  transition: all 0.3s ease;
  padding: 0;
}

.slider__nav__button:hover {
  background-color: rgba(255, 255, 255, 0.8);
}

.slider__nav__button.is-active {
  background-color: #ffffff;
}

/* Thumbnails */
.c-project-hero-block__thumbnails {
  display: flex;
  gap: 0.5rem;
  padding: 1.5rem 1.5rem 2.5rem 1.5rem;
  background-color: var(--pearl-25);
  overflow-x: auto;
  justify-content: flex-start;
  position: relative;
}

@keyframes indicatorSlide {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

.c-project-hero-block__thumbnail {
  flex: 0 0 auto;
  width: 80px;
  height: 80px;
  border: none;
  border-radius: 4px;
  padding: 0;
  background-color: transparent;
  cursor: pointer;
  transition: all 0.2s ease;
  position: relative;
}

.c-project-hero-block__thumbnail img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.c-project-hero-block__thumbnail:hover {
  opacity: 0.8;
}

.c-project-hero-block__thumbnail.is-active {
  border: none;
}

.c-project-hero-block__thumbnail.is-active::after {
  content: "";
  position: absolute;
  bottom: -1rem;
  left: 50%;
  transform: translateX(-50%);
  width: 100%;
  height: 0.5rem;
  background-color: var(--calypso);
  animation: indicatorSlide 0.4s ease-in-out;
}

/* Responsive */
@media (max-width: 768px) {
  .c-project-hero-block {
    height: 300px;
  }

  .c-project-hero-block__buttons {
    bottom: 1.5rem;
    right: 1.5rem;
    gap: 0.5rem;
  }

  .c-project-hero-block__button {
    padding: 0.5rem 1rem;
    font-size: 0.85rem;
  }

  .c-project-hero-block__button i {
    font-size: 0.9rem;
  }

  .c-project-hero-block__modal {
    padding: 1rem;
  }

  .c-project-hero-block__modal-content {
    max-width: 100%;
  }

  .c-project-hero-block__gallery {
    max-height: 400px;
  }

  .c-project-hero-block__gallery-button {
    width: 2rem;
    height: 2rem;
    font-size: 1rem;
  }

  .c-project-hero-block__gallery-button--prev {
    left: 0.5rem;
  }

  .c-project-hero-block__gallery-button--next {
    right: 0.5rem;
  }

  .c-project-hero-block__thumbnail {
    width: 70px;
    height: 70px;
  }
}

@media (max-width: 480px) {
  .c-project-hero-block {
    height: 250px;
  }

  .c-project-hero-block__buttons {
    bottom: 1rem;
    right: 1rem;
    flex-direction: column;
    gap: 0.5rem;
  }

  .c-project-hero-block__button {
    width: auto;
    padding: 0.5rem 1rem;
    font-size: 0.8rem;
  }

  .c-project-hero-block__gallery {
    max-height: 300px;
  }

  .c-project-hero-block__gallery-button {
    width: 2rem;
    height: 2rem;
    font-size: 0.9rem;
  }

  .c-project-hero-block__gallery-button--prev {
    left: 0.5rem;
  }

  .c-project-hero-block__gallery-button--next {
    right: 0.5rem;
  }

  .c-project-hero-block__thumbnails {
    padding: 1rem;
  }

  .c-project-hero-block__thumbnail {
    width: 60px;
    height: 60px;
  }
}
</style>
